import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { Site } from '@/app/types';
import { listSites } from '@/app/services/siteService';
import { UserWithSites } from '@/app/services/userManagementService';
import { ScrollArea } from '@/components/ui/scroll-area';

interface SiteAssignmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithSites | null;
  onAssignSites: (siteIds: number[]) => Promise<void>;
  organizationId: number;
}

const SiteAssignmentModal: React.FC<SiteAssignmentModalProps> = ({
  open,
  onOpenChange,
  user,
  onAssignSites,
  organizationId,
}) => {
  const [sites, setSites] = useState<Site[]>([]); // All sites in the organization
  const [selectedSites, setSelectedSites] = useState<Set<number>>(new Set()); // User's assigned sites
  const [loadingSites, setLoadingSites] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load all sites for the organization
  useEffect(() => {
    const loadSites = async () => {
      if (!open || !organizationId) return;
      
      try {
        setLoadingSites(true);
        console.log('Loading all sites for organization:', organizationId);
        // Use show_all=true to get all organization sites for user management
        const response = await listSites({ 
          limit: 100,
          show_all: true 
        }, organizationId);
        console.log('Sites loaded:', response.items);
        setSites(response.items);
      } catch (error) {
        console.error('Failed to load sites:', error);
      } finally {
        setLoadingSites(false);
      }
    };

    loadSites();
  }, [open, organizationId]);

  // Initialize selected sites based on user's current sites
  useEffect(() => {
    if (open && user) {
      console.log('Modal opened for user:', user.username);
      console.log('User sites:', user.sites);
      
      if (user.sites && user.sites.length > 0) {
        const siteIds = user.sites.map(s => s.id);
        console.log('Setting selected site IDs:', siteIds);
        setSelectedSites(new Set(siteIds));
      } else {
        console.log('User has no sites assigned, clearing selection');
        setSelectedSites(new Set());
      }
    }
  }, [open, user]);

  const handleSiteToggle = (siteId: number, checked: boolean) => {
    setSelectedSites(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(siteId);
      } else {
        newSet.delete(siteId);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onAssignSites(Array.from(selectedSites));
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update site assignments:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) {
        // Reset state when closing
        setSelectedSites(new Set());
      }
      onOpenChange(newOpen);
    }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            Assign Sites to {user.full_name || user.username}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          {loadingSites ? (
            <div className="flex justify-center items-center h-32">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : sites.length === 0 ? (
            <p className="text-center text-muted-foreground">
              No sites available in this organization.
            </p>
          ) : (
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-3">
                {sites.map((site) => (
                  <div key={site.id} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent">
                    <Checkbox
                      id={`site-${site.id}`}
                      checked={selectedSites.has(site.id)}
                      onCheckedChange={(checked) => 
                        handleSiteToggle(site.id, checked as boolean)
                      }
                    />
                    <Label
                      htmlFor={`site-${site.id}`}
                      className="flex-1 cursor-pointer"
                    >
                      <div>
                        <p className="font-medium">{site.name}</p>
                        {site.code && (
                          <p className="text-sm text-muted-foreground">
                            Code: {site.code}
                          </p>
                        )}
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || loadingSites}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Assignments'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SiteAssignmentModal;